import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HandHeart, DollarSign, CreditCard, Smartphone, ChevronRight, ChevronLeft, Copy, Check, Sparkles, ShieldAlert } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PublicHeader } from "@/components/PublicHeader";
import { useI18n } from "@/contexts/I18nContext";
import { useMember } from "@/contexts/MemberContext";
import { api } from "@/lib/api";

const PRESETS = [
  { id: "p10", amount: 10 },
  { id: "p25", amount: 25 },
  { id: "p50", amount: 50 },
  { id: "p100", amount: 100 },
];

export default function GivePage() {
  const { t, lang } = useI18n();
  const { member } = useMember();
  const [settings, setSettings] = useState(null);
  const [method, setMethod] = useState(null); // null | 'card' | 'zelle'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const { data } = await api.get("/settings"); if (!cancelled) setSettings(data); } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const stripeEnabled = settings?.stripe_enabled !== false;
  const zelleEnabled = settings?.zelle_enabled !== false;

  return (
    <div>
      <PublicHeader />
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="px-4 pt-4 space-y-4"
      >
        <div className="flex items-center gap-2 px-1">
          <HandHeart className="h-5 w-5 text-primary" />
          <h1 className="font-serif-display text-2xl font-semibold tracking-tight">{t.give.title}</h1>
        </div>
        <p className="px-1 text-sm text-muted-foreground -mt-2">{t.give.subtitle}</p>

        <AnimatePresence mode="wait">
          {!method && (
            <motion.div
              key="picker"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.18 }}
              className="space-y-3"
            >
              <div className="px-1">
                <h2 className="font-serif-display text-lg font-semibold">{t.give.methodPickerTitle}</h2>
                <p className="text-xs text-muted-foreground">{t.give.methodPickerSubtitle}</p>
              </div>

              {zelleEnabled && settings?.zelle_phone && (
                <MethodButton
                  testid="give-method-zelle"
                  onClick={() => setMethod("zelle")}
                  icon={Smartphone}
                  title={t.give.methodZelle}
                  subtitle={t.give.methodZelleSubtitle}
                  badge={t.give.methodZelleBadge}
                />
              )}
              {stripeEnabled && (
                <MethodButton
                  testid="give-method-card"
                  onClick={() => setMethod("card")}
                  icon={CreditCard}
                  title={t.give.methodCard}
                  subtitle={t.give.methodCardSubtitle}
                />
              )}
            </motion.div>
          )}

          {method === "card" && (
            <motion.div key="card" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }} transition={{ duration: 0.18 }}>
              <CardOfferingForm onBack={() => setMethod(null)} member={member} />
            </motion.div>
          )}

          {method === "zelle" && (
            <motion.div key="zelle" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }} transition={{ duration: 0.18 }}>
              <ZelleCard settings={settings} onBack={() => setMethod(null)} />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="h-2" />
      </motion.div>
    </div>
  );
}

function MethodButton({ icon: Icon, title, subtitle, onClick, badge, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="group w-full text-left rounded-2xl border border-border bg-white p-4 ring-1 ring-black/5 shadow-sm transition hover:border-primary/50 hover:shadow active:scale-[0.99]"
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Icon className="h-6 w-6" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-serif-display text-base font-semibold leading-tight">{title}</p>
            {badge && <Badge variant="default" className="rounded-full text-[10px] uppercase tracking-wide">{badge}</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition" />
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Stripe card form (extracted from previous GivePage)
// ---------------------------------------------------------------------------
function CardOfferingForm({ onBack, member }) {
  const { t } = useI18n();
  const [pkg, setPkg] = useState("p25");
  const [custom, setCustom] = useState("");
  const [note, setNote] = useState("");
  const [donorName, setDonorName] = useState(member?.full_name || "");
  const [submitting, setSubmitting] = useState(false);

  const effectiveAmount = useMemo(() => {
    if (pkg === "custom") return Number(custom) || 0;
    const found = PRESETS.find((p) => p.id === pkg);
    return found ? found.amount : 0;
  }, [pkg, custom]);

  const canSubmit = pkg !== "custom" ? true : effectiveAmount >= 1;

  const onSubmit = async () => {
    if (!canSubmit) {
      toast.error(t.give.amountTooSmall);
      return;
    }
    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const body = {
        package_id: pkg,
        custom_amount: pkg === "custom" ? Number(custom) : null,
        note,
        donor_name: donorName,
        donor_phone: member?.phone || "",
        origin_url: origin,
      };
      const { data } = await api.post("/payments/checkout", body);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout url");
      }
    } catch (err) {
      toast.error(t.common.error);
      setSubmitting(false);
    }
  };

  return (
    <Card className="rounded-2xl shadow-sm ring-1 ring-black/5">
      <CardContent className="p-5 space-y-5">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary"
          data-testid="give-card-back"
        >
          <ChevronLeft className="h-3 w-3" />
          {t.give.backToMethods}
        </button>

        <div className="space-y-2">
          <Label className="text-sm font-medium">{t.give.pickAmount}</Label>
          <div className="grid grid-cols-2 gap-2">
            {PRESETS.map((p) => {
              const active = pkg === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`give-preset-amount-${p.amount}`}
                  onClick={() => setPkg(p.id)}
                  className={`relative h-14 rounded-2xl border text-base font-semibold transition ${
                    active
                      ? "border-primary bg-primary/5 text-primary ring-2 ring-primary/20"
                      : "border-border bg-white text-foreground hover:border-primary/50"
                  }`}
                >
                  ${p.amount}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => setPkg("custom")}
            data-testid="give-other-amount-toggle"
            className={`mt-1 w-full rounded-2xl border h-14 text-left px-4 transition ${
              pkg === "custom"
                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                : "border-border bg-white hover:border-primary/50"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium">
                <DollarSign className="h-4 w-4 text-primary" />
                {t.give.custom}
              </div>
              {pkg === "custom" && (
                <Input
                  data-testid="give-custom-amount-input"
                  autoFocus
                  inputMode="decimal"
                  type="number"
                  min={1}
                  step="0.01"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="0.00"
                  className="ml-2 h-9 max-w-[120px] text-right"
                />
              )}
            </div>
          </button>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="give-donor-name" className="text-sm font-medium">{t.give.donorName}</Label>
          <Input
            id="give-donor-name"
            data-testid="give-donor-name-input"
            value={donorName}
            onChange={(e) => setDonorName(e.target.value)}
            className="h-12"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="give-note" className="text-sm font-medium">{t.give.noteLabel}</Label>
          <Textarea
            id="give-note"
            data-testid="give-note-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t.give.notePlaceholder}
            rows={3}
          />
        </div>

        <Button
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
          className="h-12 w-full text-base"
          data-testid="give-submit-button"
        >
          {submitting ? t.give.submitting : `${t.give.submit} \u00b7 $${effectiveAmount.toFixed(2)}`}
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Zelle informational card with tap-to-copy
// ---------------------------------------------------------------------------
function ZelleCard({ settings, onBack }) {
  const { t, lang } = useI18n();
  const phone = settings?.zelle_phone || "";
  const email = settings?.zelle_email || "";
  const name = settings?.zelle_name || "Iglesia Nueva Cancion Lancaster";
  const extra = lang === "es" ? (settings?.zelle_instructions_es || "") : (settings?.zelle_instructions_en || "");

  const [copied, setCopied] = useState("");

  const copy = async (value, key) => {
    try {
      const clean = String(value).replace(/[^\d+@.A-Za-z_-]/g, "");
      await navigator.clipboard.writeText(clean);
      setCopied(key);
      toast.success(t.give.zelleCopied);
      setTimeout(() => setCopied(""), 1800);
    } catch (_) {
      toast.error(t.common.error);
    }
  };

  return (
    <Card className="rounded-2xl shadow-sm ring-1 ring-black/5 overflow-hidden">
      <div
        className="px-5 py-5"
        style={{
          background:
            "radial-gradient(700px circle at 90% 10%, rgba(200,30,58,0.10) 0%, transparent 55%), linear-gradient(180deg, hsl(var(--accent)) 0%, hsl(var(--card)) 100%)",
        }}
      >
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary mb-3"
          data-testid="give-zelle-back"
        >
          <ChevronLeft className="h-3 w-3" />
          {t.give.backToMethods}
        </button>
        <div className="flex items-center gap-3">
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white ring-1 ring-black/5 text-primary">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="font-serif-display text-lg font-semibold leading-tight">{t.give.zelleTitle}</p>
            <p className="text-xs text-muted-foreground">{t.give.zelleBody}</p>
          </div>
        </div>
      </div>

      <CardContent className="p-5 space-y-4">
        {/* Send-to block */}
        <div className="rounded-2xl border border-border bg-white p-4 space-y-3">
          {phone && (
            <CopyRow label={t.give.zelleSendTo} value={phone} kind="phone" onCopy={() => copy(phone.replace(/\D/g, ""), "phone")} copied={copied === "phone"} />
          )}
          {email && (
            <CopyRow label={"Email"} value={email} kind="email" onCopy={() => copy(email, "email")} copied={copied === "email"} />
          )}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t.give.zelleRegisteredTo}</p>
            <p className="font-serif-display text-base font-semibold">{name}</p>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t.give.zelleSteps}</p>
          <ol className="space-y-2">
            {[t.give.zelleStep1, t.give.zelleStep2, t.give.zelleStep3, t.give.zelleStep4, t.give.zelleStep5].map((step, i) => (
              <li key={i} className="flex items-start gap-3 rounded-xl bg-secondary/40 p-3">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">{i + 1}</span>
                <span className="text-sm leading-snug">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {extra && (
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm whitespace-pre-line">
            {extra}
          </div>
        )}

        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-amber-900">
          <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide">{t.give.zelleNoteHeader}</p>
            <p className="text-sm leading-snug">{t.give.zelleNoteBody}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CopyRow({ label, value, onCopy, copied, kind }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="mt-1 flex items-center justify-between gap-2">
        <p className="font-serif-display text-lg font-semibold tracking-wide" data-testid={`zelle-${kind}-value`}>{value}</p>
        <Button size="sm" variant="outline" onClick={onCopy} className="rounded-full gap-1" data-testid={`zelle-copy-${kind}`}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          <span className="text-xs">{copied ? "OK" : "Copiar"}</span>
        </Button>
      </div>
    </div>
  );
}
